import type { ReactElement } from 'react';
import {
  TOOL_APPROVAL_FILTERS,
  type ToolApprovalCounts,
  type ToolApprovalFilter,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const OPTIONS: ReadonlyArray<{
  value: ToolApprovalFilter;
  label: string;
  countKey: keyof ToolApprovalCounts;
}> = [
  {
    value: TOOL_APPROVAL_FILTERS.NOT_APPROVED,
    label: 'Not approved',
    countKey: 'notApproved',
  },
  {
    value: TOOL_APPROVAL_FILTERS.APPROVED,
    label: 'Approved',
    countKey: 'approved',
  },
  { value: TOOL_APPROVAL_FILTERS.ALL, label: 'All', countKey: 'all' },
];

export interface ToolApprovalShowFilterProps {
  value: ToolApprovalFilter;
  onChange: (next: ToolApprovalFilter) => void;
  /** Tab totals; counts are omitted from the labels until they load. */
  counts?: ToolApprovalCounts;
  className?: string;
}

/**
 * KIR-4854: "Show: Not approved (N) | Approved (N) | All (N)" segmented control
 * rendered in the Shadow AI Tools table toolbars. Fully controlled.
 */
export function ToolApprovalShowFilter({
  value,
  onChange,
  counts,
  className,
}: ToolApprovalShowFilterProps): ReactElement {
  return (
    <div
      className={cn('flex items-center gap-2', className)}
      data-slot="tool-approval-show-filter"
    >
      <span className="text-xs font-medium text-muted-foreground">Show:</span>
      <div
        role="group"
        aria-label="Show tools by approval"
        className="inline-flex items-center"
      >
        {OPTIONS.map((option, index) => {
          const selected = option.value === value;
          const count = counts?.[option.countKey];
          return (
            <Button
              key={option.value}
              type="button"
              size="sm"
              variant={selected ? 'default' : 'outline'}
              aria-pressed={selected}
              onClick={() => {
                if (!selected) onChange(option.value);
              }}
              className={cn(
                'h-8 rounded-none px-3 text-xs font-medium shadow-none',
                index === 0 && 'rounded-l-md',
                index === OPTIONS.length - 1 && 'rounded-r-md',
                index > 0 && '-ml-px',
                selected && 'relative z-10'
              )}
            >
              {option.label}
              {count !== undefined && ` (${count})`}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
